import React, { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Checkbox, FormControl, FormHelperText, ListItemText, MenuItem, Select } from "@material-ui/core";
import makeStyles from "@material-ui/styles/makeStyles";
import { themeOptions } from "styles/theme";

import { format } from "../../lib/globalize-util";
import { IAttribute } from "../../constants/product-customizations/customization";
import { TClosedRange } from "constants/product-customizations/types";
import { CUSTOMIZATIONS } from "../../constants/integration/customizations";

export const ATTRIBUTES_DROPDOWN_KEY = "attributesDropdownKey";

export interface IAttributesDropdownProps {
    attributes: IAttribute[];
    attributeLimit: TClosedRange;
    selectedAttributes?: IAttribute[];
    onChange: (selectedAttributes: IAttribute[]) => void;
    attributesAmountValidationError: string | null;
    onValidationError?: (id: string, error: string | null) => void;
}

export const AttributesDropdown: React.FC<IAttributesDropdownProps> = ({
    attributes,
    onChange,
    attributeLimit,
    selectedAttributes,
    onValidationError,
    attributesAmountValidationError,
}) => {
    const classes = useStyles();
    const { menuStyles, menuListStyles, errorMenuStyles, errorMessageStyles, dropdownInputStyles } = muiStyles;
    const [selectedAttributeNames, setSelectedAttributeNames] = useState<string[]>([]);
    const [validationError, setValidationError] = useState<string | null>(null);
    const attributesMap = useMemo(() => _.keyBy(attributes, "name"), [attributes]);
    const attributeNames = useMemo(() => Object.keys(attributesMap), [attributesMap]);
    const hasValidationErrors = !!validationError || !!attributesAmountValidationError;

    const updateValidationError = (selectedAttributesLength: number): void => {
        const { min } = attributeLimit;
        const currentValidationError =
            selectedAttributesLength < min
                ? format("At least |count| attributes should be selected", { count: min })
                : null;

        setValidationError(currentValidationError);
        onValidationError?.(ATTRIBUTES_DROPDOWN_KEY, currentValidationError);
    };

    const handleChangeMultiple = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const value: unknown = event?.target?.value;
        if (!Array.isArray(value)) {
            return;
        }

        const attributeNames: string[] = value;
        const selectedAttributesLength = attributeNames.length;
        const { max } = attributeLimit;
        if (selectedAttributesLength > max) {
            setValidationError(format("At most |count| attributes can be selected", { count: max }));

            return;
        }

        updateValidationError(selectedAttributesLength);

        const selectedAttributes = attributeNames.reduce((acc, curr) => {
            acc.push(attributesMap[curr]);

            return acc;
        }, [] as IAttribute[]);

        setSelectedAttributeNames(attributeNames);
        onChange(selectedAttributes);
    };

    const handleDropdownClose = useCallback(() => setValidationError(null), []);
    const renderSelectValue = useCallback((value: unknown): React.ReactNode => {
        if (!Array.isArray(value)) {
            return null;
        }

        return value.length ? value.join(", ") : format("Multi-select");
    }, []);

    useEffect(() => {
        setSelectedAttributeNames(selectedAttributes?.map((attr) => attr.name) ?? []);
    }, [selectedAttributes]);

    return (
        <>
            <div className={classes.dropdownLabel}>{format("Customizations")}</div>
            <FormControl className={classes.formControl} error={hasValidationErrors}>
                <Select
                    multiple={true}
                    autoWidth={true}
                    displayEmpty={true}
                    placeholder={format("Multi-select")}
                    style={dropdownInputStyles}
                    MenuProps={{
                        style: menuStyles,
                        MenuListProps: {
                            style: hasValidationErrors ? { ...menuListStyles, ...errorMenuStyles } : menuListStyles,
                        },
                    }}
                    onChange={handleChangeMultiple}
                    onClose={handleDropdownClose}
                    value={selectedAttributeNames}
                    renderValue={renderSelectValue}
                    data-test={CUSTOMIZATIONS.ATTRIBUTES_DROPDOWN}
                >
                    {attributeNames.map((attr, index) => (
                        <MenuItem
                            className={classes.menuItem}
                            key={attr}
                            value={attr}
                            data-test={`${CUSTOMIZATIONS.ATTRIBUTES_DROPDOWN_ITEM}_${index}`}
                        >
                            <Checkbox
                                data-test={`${CUSTOMIZATIONS.ATTRIBUTES_DROPDOWN_ITEM_CHECKBOX}_${index}`}
                                className={classes.checkbox}
                                checked={selectedAttributeNames.indexOf(attr) > -1}
                            />
                            <ListItemText primary={attr} />
                        </MenuItem>
                    ))}

                    <FormHelperText
                        data-test={CUSTOMIZATIONS.ATTRIBUTES_DROPDOWN_NOTIFICATION}
                        style={errorMessageStyles}
                    >
                        {validationError || attributesAmountValidationError}
                    </FormHelperText>
                </Select>
            </FormControl>
        </>
    );
};

const muiStyles = {
    menuStyles: {
        zIndex: 1800,
    },
    menuListStyles: {
        width: "250px",
    },
    errorMenuStyles: { border: "1px solid red" },
    errorMessageStyles: {
        padding: "5px 16px",
        fontSize: "12px",
        color: "red",
    },
    dropdownInputStyles: {
        height: "36px",
        width: "300px",
        minWidth: "180px",
        color: themeOptions.palette.grey,
    },
};

const useStyles = makeStyles({
    formControl: {
        marginBottom: "10px",
    },
    checkbox: {
        backgroundColor: "transparent !important", // force disable ripple effect
        color: themeOptions.palette.grey,
        "& .MuiSvgIcon-root": {
            fontSize: 22,
        },
        "&.Mui-checked": {
            color: themeOptions.palette.primary1Color,
        },
    },
    dropdownLabel: {
        color: themeOptions.palette.offBlack,
        fontSize: "14px",
    },
    menuItem: {
        maxWidth: "247px",
        height: "40px",
    },
});
